import { useMemo, useState } from 'react';
import { Shippings } from '@app/pages/checkout-app/types';
import { ICheckoutActions } from '../../../models/ICheckoutActions';
import {
  DeliveryMethodDisplayOrder,
  DeliveryMethodSection,
  buildDeliveryMethodDisplayOrder,
  reconcileDisplayOrderWithExistingMethods,
  toDisplayOrderPayload,
  keepDraftEditedDuringSave,
} from './deliveryMethodDisplayOrder';

interface UseDeliveryMethodDisplayOrderParameters {
  checkoutGroupId: number;
  savedSections: DeliveryMethodSection[] | null | undefined;
  orderableDeliveryMethods: Shippings[];
  checkoutActions: ICheckoutActions;
}

export const useDeliveryMethodDisplayOrder = ({
  checkoutGroupId,
  savedSections,
  orderableDeliveryMethods,
  checkoutActions,
}: UseDeliveryMethodDisplayOrderParameters) => {
  const [draftDisplayOrder, setDraftDisplayOrder] = useState<DeliveryMethodDisplayOrder | null>(null);
  const [isSavingDisplayOrder, setIsSavingDisplayOrder] = useState(false);
  const [collapsedSectionKeys, setCollapsedSectionKeys] = useState<Set<string>>(new Set());

  const savedDisplayOrder = useMemo(
    () => buildDeliveryMethodDisplayOrder(orderableDeliveryMethods, savedSections ?? []),
    [orderableDeliveryMethods, savedSections],
  );

  const existingMethodIds = useMemo(
    () => orderableDeliveryMethods.map((deliveryMethod) => String(deliveryMethod.id)),
    [orderableDeliveryMethods],
  );

  const displayOrder = useMemo(
    () =>
      draftDisplayOrder
        ? reconcileDisplayOrderWithExistingMethods(draftDisplayOrder, existingMethodIds)
        : savedDisplayOrder,
    [draftDisplayOrder, existingMethodIds, savedDisplayOrder],
  );

  const applyDisplayOrderChange = (
    changeDisplayOrder: (currentDisplayOrder: DeliveryMethodDisplayOrder) => DeliveryMethodDisplayOrder,
  ) => {
    setDraftDisplayOrder((currentDraft) =>
      changeDisplayOrder(
        currentDraft ? reconcileDisplayOrderWithExistingMethods(currentDraft, existingMethodIds) : savedDisplayOrder,
      ),
    );
  };

  const saveDisplayOrder = async () => {
    const draftBeingSaved = draftDisplayOrder;
    setIsSavingDisplayOrder(true);
    const wasSaved = await checkoutActions.saveDeliveryMethodDisplayOrder(
      checkoutGroupId,
      toDisplayOrderPayload(displayOrder),
    );
    setIsSavingDisplayOrder(false);
    if (wasSaved) {
      setDraftDisplayOrder((currentDraft) => keepDraftEditedDuringSave(currentDraft, draftBeingSaved));
    }
  };

  const toggleSectionCollapsed = (sectionKey: string) => {
    setCollapsedSectionKeys((currentCollapsedSectionKeys) => {
      const nextCollapsedSectionKeys = new Set(currentCollapsedSectionKeys);
      if (!nextCollapsedSectionKeys.delete(sectionKey)) {
        nextCollapsedSectionKeys.add(sectionKey);
      }
      return nextCollapsedSectionKeys;
    });
  };

  return {
    displayOrder,
    hasUnsavedDisplayOrderChanges: draftDisplayOrder !== null,
    isSavingDisplayOrder,
    applyDisplayOrderChange,
    saveDisplayOrder,
    discardDisplayOrderChanges: () => setDraftDisplayOrder(null),
    collapsedSectionKeys,
    toggleSectionCollapsed,
  };
};
